import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ExternalLink, FileText, User } from 'lucide-react';
import {
  formatAgentDraftAmount,
  formatAgentDraftDate,
} from '../utils/agentDocumentDraftView';

const QUICK_ACTION_KEYS = [
  { id: 'add-line', labelKey: 'add_line' },
  { id: 'change-date', labelKey: 'change_date' },
  { id: 'change-client', labelKey: 'change_client' },
];

export default function AIAgentDocumentDraftCard({
  draft,
  lightSurface = false,
  onOpenEditor,
  onQuickAction,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.documentDraftCard.${key}`, opts);

  const quickActions = useMemo(
    () =>
      QUICK_ACTION_KEYS.map((action) => ({
        id: action.id,
        label: p(`quick_actions.${action.labelKey}`),
      })),
    [t],
  );

  if (!draft) return null;

  const surface = lightSurface
    ? 'bg-white border-gray-200 text-gray-800'
    : 'bg-white/10 border-white/20 text-white/95';

  const muted = lightSurface ? 'text-gray-500' : 'text-white/55';
  const chipClass = lightSurface
    ? 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-blue-50 hover:border-blue-200'
    : 'border-white/15 bg-white/5 text-white/80 hover:bg-white/10 hover:border-white/25';

  return (
    <div className={`mt-3 rounded-2xl border p-4 ${surface}`}>
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`shrink-0 p-2 rounded-xl border ${
            lightSurface ? 'bg-blue-50 border-blue-100' : 'bg-blue-500/15 border-blue-400/25'
          }`}
        >
          <FileText className={`h-5 w-5 ${lightSurface ? 'text-blue-600' : 'text-blue-300'}`} />
        </div>
        <div className="min-w-0">
          <p className="m-0 font-semibold text-sm">
            {p('intro', { docLabel: draft.docLabel })}
          </p>
          <p className={`m-0 mt-1 text-xs ${muted}`}>{p('verify_hint')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
        <div className={`rounded-xl border px-3 py-2 ${lightSurface ? 'border-gray-100' : 'border-white/10'}`}>
          <span className={`flex items-center gap-1.5 text-xs ${muted}`}>
            <User className="h-3.5 w-3.5" />
            {p('client')}
          </span>
          <p className="m-0 mt-0.5 font-medium truncate">{draft.partner?.name || '—'}</p>
        </div>
        <div className={`rounded-xl border px-3 py-2 ${lightSurface ? 'border-gray-100' : 'border-white/10'}`}>
          <span className={`flex items-center gap-1.5 text-xs ${muted}`}>
            <Calendar className="h-3.5 w-3.5" />
            {p('date')}
          </span>
          <p className="m-0 mt-0.5 font-medium">{formatAgentDraftDate(draft.date)}</p>
        </div>
        <div className={`rounded-xl border px-3 py-2 ${lightSurface ? 'border-gray-100' : 'border-white/10'}`}>
          <span className={`block text-xs ${muted}`}>{p('total_amount')}</span>
          <p className="m-0 mt-0.5 font-semibold text-emerald-400">
            {formatAgentDraftAmount(draft.totals?.totalTtc)}
          </p>
        </div>
        <div className={`rounded-xl border px-3 py-2 ${lightSurface ? 'border-gray-100' : 'border-white/10'}`}>
          <span className={`block text-xs ${muted}`}>{p('lines')}</span>
          <p className="m-0 mt-0.5 font-medium">{draft.lineCount || 0}</p>
        </div>
      </div>

      <p className={`m-0 mb-3 text-xs ${muted}`}>{p('modify_prompt')}</p>

      <div className="flex flex-wrap gap-2 mb-3">
        {quickActions.map((action) => (
          <button
            key={action.id}
            type="button"
            onClick={() => onQuickAction?.(action.id, draft)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${chipClass}`}
          >
            {action.label}
          </button>
        ))}
      </div>

      {onOpenEditor && draft.openPath && (
        <button
          type="button"
          onClick={() => onOpenEditor(draft)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            lightSurface
              ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100'
              : 'border-blue-400/40 bg-blue-500/20 text-blue-100 hover:bg-blue-500/30'
          }`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {p('show_form')}
        </button>
      )}
    </div>
  );
}
